import config from '@config'
import useDisableThirdPartyScripts from './useDisableThirdPartyScripts'
import { useQuery } from 'react-query'
import { formatDate } from '@utils/date'
import { getTextFromHTMLElement } from '@utils/html'

export interface ScribblePost {
  Id: number
  PostMeta: {
    CreationDate: number
  }

  [key: string]: any
}

export interface FormattedDate {
  date: string
  time: string
  dateOrTime: string
  sameDateTime: string | null
}

export interface ProcessedPost {
  id: number
  dateInfo: FormattedDate
  text: string
}

export interface ScribbleAPIResult {
  posts: ScribblePost[]
  streamMeta: {
    IsLive: boolean
  }
}

interface UseScribblePollingProps {
  streamId?: string
}

type FetchAndReturnDataProps = Required<
  Pick<UseScribblePollingProps, 'streamId'>
>

const {
  publication: { publication },
  scribble: {
    options: { pageSizeTeaser, pollInterval },
    token: scribbleToken,
  },
} = config

const formatNewstickerPosts = ({
  Id,
  CreationDate,
  Type,
  Content,
}: ScribblePost): ProcessedPost => {
  const dateInfo = formatDate(CreationDate) as FormattedDate
  let minutesWordPlural
  let minutesWordSingular
  let replacement

  if (publication === 'romandie') {
    minutesWordPlural = 'minutes'
    minutesWordSingular = 'minute'
    replacement = 'min.'
  } else {
    minutesWordPlural = 'Minuten'
    minutesWordSingular = 'Minute'
    replacement = 'Min.'
  }

  dateInfo.dateOrTime = dateInfo.dateOrTime
    .replace(minutesWordPlural, replacement)
    .replace(minutesWordSingular, replacement)

  return {
    id: Id,
    dateInfo,
    text: Type === 'TEXT' ? Content : getTextFromHTMLElement(Content),
  }
}

const latestPostSorter = (
  { PostMeta: { CreationDate: postACreationDate } }: ScribblePost,
  { PostMeta: { CreationDate: postBCreationDate } }: ScribblePost
) => postBCreationDate - postACreationDate

const getScribbleData = async ({ streamId }: FetchAndReturnDataProps) => {
  const payload = (await (
    await fetch(
      `https://api.scribblelive.com/v1/stream/${streamId}/posts?PageNumber=0&PageSize=${pageSizeTeaser}&NewestAtBottom=false&IncludeComments=false&token=${scribbleToken}`
    )
  ).json()) as unknown as ScribbleAPIResult

  const { posts } = payload

  if (posts.length > 0) {
    return {
      isLive: !!payload?.streamMeta?.IsLive,
      posts: [...posts]
        .sort(latestPostSorter)
        .slice(0, pageSizeTeaser)
        .map((post) => formatNewstickerPosts(post)),
    }
  } else {
    throw new Error(`Invalid scribble data for streamId: ${streamId}`)
  }
}

const useNewstickerScribblePolling = ({
  streamId = '',
}: UseScribblePollingProps) => {
  const disableThirdPartyScripts = useDisableThirdPartyScripts()

  return useQuery(
    ['newstickerScribblePosts', streamId],
    () =>
      getScribbleData({
        streamId,
      }),
    {
      enabled: !disableThirdPartyScripts && !!streamId,
      onError: (err) => {
        console.error({
          reason: `scribble hook loading ${streamId} failed`,
          error: err,
        })
      },
      refetchInterval: (data) => (data?.isLive ? pollInterval : false),
      refetchOnWindowFocus: false,
    }
  )
}

export default useNewstickerScribblePolling
